<template>
  <main class="main-content">
    <component :is="navItem"></component>
    <div class="agent-panel">
      <div class="agent-header">
        <div class="icon-container">
          <i class="fas fa-user"></i>
        </div>
        <div class="header-text">
          <h2>{{ userTypeDisplayName }}</h2>
          <p class="subtitle">Enter user details to create new {{ userTypeDisplayName.toLowerCase() }}</p>
        </div>
      </div>

      <div class="user-details-section">
        <div class="input-group">
          <label for="name">Full Name <span class="required">*</span></label>
          <div class="input-with-icon">
            <i class="fas fa-user input-icon"></i>
            <input id="name" type="text" v-model="user.userFullName" placeholder="Enter full name" required>
          </div>
        </div>

        <div class="input-group">
          <label for="phone">Phone Number <span class="required">*</span></label>
          <div class="input-with-icon phone-input">
            <span class="phone-prefix">+254</span>
            <input id="phone" type="tel" v-model="phoneNumber" @input="formatPhoneNumber" placeholder="725931863" maxlength="10" required>
          </div>
          <small class="field-note">Enter 9 digits (without leading 0) or 10 digits (with leading 0)</small>
        </div>

        <div class="input-group">
          <label for="email">Email Address <span class="required">*</span></label>
          <div class="input-with-icon">
            <i class="fas fa-envelope input-icon"></i>
            <input id="email" type="email" v-model="user.email" placeholder="Enter valid email address" required>
          </div>
          <small class="field-note">Must be a valid email address</small>
        </div>

        <div class="input-group">
          <label for="agentCode">Agent Code</label>
          <div class="input-with-icon">
            <i class="fas fa-id-badge input-icon"></i>
            <input id="agentCode" type="number" v-model="user.agentCode" placeholder="Enter agent code (optional)" min="0">
          </div>
          <small class="field-note">Leave as 0 for system assignment</small>
        </div>

      </div>

      <div class="action-buttons">
        <button class="btn cancel" @click="cancel">Cancel</button>
        <button class="btn create" @click="createUser" :disabled="inviting || !isFormValid">
          <span v-if="inviting">
            <i class="fas fa-spinner fa-spin"></i>
            Creating...
          </span>
          <span v-else>Create User</span>
        </button>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import navItem from '@/admin/components/nav/NavItem.vue';
import { isValidEmail as validateEmailFormat } from '@/utils/emailValidation';

export default {
  name: 'RegisterUser',
  components: { navItem },
  props: {
    selectedUserType: {
      type: String,
      default: 'agents',
    },
  },
  data() {
    return {
      inviting: false,
      phoneNumber: '',
      user: {
        userFullName: '',
        telephone: '',
        email: '',
        userType: 'A',
        agentCode: 0,
      },
    };
  },
  computed: {
    navItem() {
      return navItem;
    },
    isFormValid() {
      const userDetailsValid = this.user.userFullName && this.user.telephone && this.user.email;
      const emailValid = this.user.email && this.isValidEmail(this.user.email);
      const phoneValid = this.user.telephone && this.user.telephone.length >= 12; // +254 + 9 digits
      return userDetailsValid && emailValid && phoneValid;
    },
    userTypeDisplayName() {
      const typeMap = {
        agents: 'Agent User',
        bancassurance: 'Bancassurance User',
        apaUsers: 'APA User',
        admins: 'Admin User',
      };
      return typeMap[this.selectedUserType] || 'User';
    },
    computedUserType() {
      if (this.selectedUserType === 'agents' || this.selectedUserType === 'bancassurance') {
        return 'A';
      }
      if (this.selectedUserType === 'apaUsers' || this.selectedUserType === 'admins') {
        return 'S';
      }
      return 'A';
    },
  },
  watch: {
    'user.telephone': {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.startsWith('+254')) {
          this.phoneNumber = newVal.substring(4);
        }
      },
    },
  },
  methods: {

    cancel() {
      this.$router.push({ name: 'UserManagement' });
    },

    isValidEmail(email) {
      return validateEmailFormat(email);
    },

    formatPhoneNumber() {
      let cleaned = this.phoneNumber.replace(/\D/g, '');

      if (cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1);
      }

      if (cleaned.length > 9) {
        cleaned = cleaned.substring(0, 9);
      }

      this.phoneNumber = cleaned;

      if (cleaned.length === 9) {
        this.user.telephone = `+254${cleaned}`;
      } else {
        this.user.telephone = cleaned ? `+254${cleaned}` : '';
      }
    },

    async createUser() {
      if (!this.validateUserData()) {
        return;
      }

      this.inviting = true;

      try {
        await this.submitUserRegistration();
        this.handleRegistrationSuccess();
      } catch (error) {
        this.handleRegistrationError(error);
      } finally {
        this.inviting = false;
      }
    },

    validateUserData() {
      if (!this.isFormValid) {
        this.showMessage('error', 'Please fill in all required fields with valid data.');
        return false;
      }

      if (!this.isValidEmail(this.user.email)) {
        this.showMessage('error', 'Please enter a valid email address');
        return false;
      }

      if (!this.user.telephone || this.user.telephone.length < 12) {
        this.showMessage('error', 'Please enter a valid phone number');
        return false;
      }

      return true;
    },

    async submitUserRegistration() {
      const payload = {
        userType: this.computedUserType,
        userFullName: this.user.userFullName,
        email: this.user.email,
        telephone: this.user.telephone,
        agentCode: parseInt(this.user.agentCode, 10) || 0,
      };

      const response = await axios.post('/lmsagent/login/RegisterSystemUser', payload);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Registration failed');
      }

      return response;
    },

    handleRegistrationSuccess() {
      this.showMessage('success', 'User registered successfully! An email with password setup instructions has been sent to the user.');
      this.resetForm();

      setTimeout(() => {
        this.$router.push({ name: 'UserManagement' });
      }, 3000);
    },

    handleRegistrationError(error) {
      const errorMessage = this.getErrorMessage(error);
      this.showMessage('error', errorMessage);
    },

    getErrorMessage(error) {
      let errorMessage = 'Failed to create user. Please try again.';

      if (error.response?.data) {
        const apiError = error.response.data;
        if (typeof apiError === 'string') {
          errorMessage = apiError;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }

        if (errorMessage.includes('User Already exists') || errorMessage.includes('already exists')) {
          errorMessage = 'A user with this email address already exists.';
        } else if (errorMessage.includes('bad email')) {
          errorMessage = 'Invalid email address format.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your network connection.';
      }

      return errorMessage;
    },

    showMessage(type, message) {
      if (this.$toast && this.$toast[type]) {
        this.$toast[type](message);
      } else {
        // eslint-disable-next-line no-alert
        alert(message);
      }
    },

    resetForm() {
      this.phoneNumber = '';
      this.user = {
        userFullName: '',
        telephone: '',
        email: '',
        userType: 'A',
        agentCode: 0,
      };
    },
  },
};
</script>
<style scoped>
.main-content {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
  background-color: #f0f2f5;
  left: 16%;
  margin-top: 4%;
  width: 100%;
}

.agent-panel {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.agent-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.agent-header h2 {
  font-size: 1.5rem;
  color: #164194;
  font-weight: 600;
  margin: 0;
}

.subtitle {
  color: #666;
  font-size: 0.95rem;
  margin-top: 0.25rem;
}

.icon-container {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #164194;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
  font-size: 1.2rem;
}

.user-details-section {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
}

.input-group label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #555;
  margin-bottom: 0.5rem;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #164194;
  font-size: 1rem;
}

.input-with-icon input {
  width: 100%;
  padding: 0.8rem 1rem 0.8rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s;
  box-sizing: border-box;
}

.input-with-icon input:focus {
  border-color: #164194;
  box-shadow: 0 0 0 2px rgba(22, 65, 148, 0.1);
  outline: none;
}

.input-with-icon input::placeholder {
  color: #aaa;
}

.status-toggle {
  display: flex;
  align-items: center;
}

.status-label {
  margin-right: 1rem;
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
}

.switch-group {
  display: flex;
  flex-direction: column;
}

.divider {
  height: 1px;
  background-color: #e0e0e0;
  margin: 2rem 0;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #164194;
  margin-bottom: 0.5rem;
}

.section-header p {
  font-size: 0.9rem;
  color: #666;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  color: #666;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #164194;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  padding: 2rem;
  text-align: center;
}

.error-message {
  color: #e74c3c;
  background-color: #fceded;
  border: 1px solid #e74c3c;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.error-message i {
  font-size: 2rem;
}

.retry-btn {
  padding: 0.5rem 1rem;
  background: #164194;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}

.retry-btn:hover {
  background: #0d3380;
}

.search-container {
  margin-bottom: 1.5rem;
}

.search-box {
  max-width: 350px;
}

.search-input {
  width: 100%;
  padding: 0.8rem 1rem 0.8rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  box-sizing: border-box;
}

.roles-table-container {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}

.roles-table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.roles-table thead {
  background: linear-gradient(135deg, #164194 0%, #164194 100%);
  color: white;
}

.roles-table th {
  padding: 1rem 0.75rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.module-header {
  text-align: left !important;
  padding-left: 1.5rem !important;
  min-width: 200px;
}

.privilege-header {
  min-width: 220px;
}

.roles-table tbody tr {
  background: #f9f9f9;
  border-bottom: 1px solid #e7e7e7;
}

.roles-table tbody tr:nth-child(even) {
  background: #ffffff;
}

.roles-table tbody tr:hover {
  background: #eef6ff;
}

.disabled-row {
  background: #e9ecef !important;
  color: #6c757d !important;
  cursor: not-allowed;
}

.disabled-row:hover {
  background: #e2e6ea !important;
}

.disabled-row .module-cell span,
.disabled-row .permission-cell .disabled-indicator,
.disabled-row .privilege-cell .disabled-indicator {
  color: #6c757d;
}

.disabled-row .module-check {
  color: #adb5bd !important;
}

.roles-table td {
  padding: 1rem 0.75rem;
  text-align: center;
  vertical-align: middle;
  color: #333;
}

.module-cell {
  text-align: left !important;
  padding-left: 1.5rem !important;
  font-weight: 500;
  color: #222;
  display: flex;
  align-items: center;
}

.module-check {
  margin-right: 0.75rem;
  color: #28a745;
  font-size: 0.9rem;
}

.permission-check {
  color: #28a745; /* Green */
  font-size: 1.1rem;
}

.permission-cross {
  color: #dc3545; /* Red */
  font-size: 1.1rem;
}

.disabled-indicator {
  color: #6c757d;
  font-style: italic;
}

.privilege-cell {
  padding: 0.5rem !important;
}

.dropdown-container {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 200px;
}

.privilege-dropdown {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 0.85rem;
  appearance: none;
  cursor: pointer;
  box-sizing: border-box;
}

.privilege-dropdown:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.dropdown-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
  font-size: 0.8rem;
}

.no-results-message {
  text-align: center;
  padding: 2rem;
  color: #555;
  font-style: italic;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 0.9rem;
  color: #666;
  margin-top: 1rem;
  margin-bottom: 2rem;
}

.pagination select {
  margin: 0 0.5rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  outline: none;
  background-color: white;
}

.pagination-controls {
  margin-left: 1rem;
  display: flex;
}

.pagination-controls button {
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: #fff;
  margin: 0 0.25rem;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-controls button:hover:not(:disabled) {
  background-color: #f0f0f0;
  border-color: #ccc;
}

.pagination-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  margin-top: 2rem;
}

.btn {
  padding: 0.85rem 2rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.3s;
  min-width: 120px;
  text-align: center;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #adb5bd !important;
  color: #f8f9fa !important;
}

.btn.cancel {
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #555;
  margin-right: 1rem;
}

.btn.create {
  background: #164194;
  color: white;
}

.btn.cancel:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #ccc;
}

.btn.create:hover:not(:disabled) {
  background: #0d3380;
}

.required {
  color: #e74c3c;
  font-weight: bold;
}

.field-note {
  color: #666;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
}

.phone-input {
  position: relative;
  display: flex !important;
  align-items: center;
}

.phone-prefix {
  position: absolute;
  left: 40px;
  color: #164194;
  font-weight: 500;
  font-size: 0.95rem;
  z-index: 1;
  pointer-events: none;
}

.phone-input input {
  padding-left: 80px !important;
}

.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #164194;
}

input:focus + .slider {
  box-shadow: 0 0 1px #164194;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.slider.round {
  border-radius: 26px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>
